/**
 * MINI-JEU — TAPE LA CRÉATURE ! (façon tape-taupe). Des créatures de l'enfant
 * surgissent de 9 terriers : taper une créature = +1 point, taper une bombe = -1 vie
 * (3 vies ; en rater une ne coûte RIEN). 3 niveaux de 8 points (24 = victoire) :
 * niveau 1 lent SANS bombe (confiance, cf. retour Pat sur Attrape), puis ça accélère.
 * Victoire → /api/child/minigame/reward (70% créature). Thémé auto via --kid-*.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createGameAudio, type GameAudio } from "./gameSounds";

type Drop = { emoji: string; name: string; rarity: string } | null;
type Pop = { id: number; emoji: string; bomb: boolean } | null;

const HOLES = 9;
const LEVEL_GOAL = 8; // points par niveau
const LEVELS = 3; // 3 × 8 = 24 points pour gagner
// Par niveau : durée d'affichage d'une créature, cadence d'apparition, proba bombe.
const TUNING = [
  { popMs: 1700, spawnMs: 950, bombP: 0 },
  { popMs: 1350, spawnMs: 800, bombP: 0.22 },
  { popMs: 1100, spawnMs: 700, bombP: 0.3 },
];
const FALLBACK = ["🦖", "🦄", "🐉", "🦋", "🐢", "🚀", "🤖", "🌟"];

export default function TapGame({ apiBase, onClose }: { apiBase: string; onClose: () => void }) {
  const [faces, setFaces] = useState<string[]>(FALLBACK);
  const [holes, setHoles] = useState<Pop[]>(Array(HOLES).fill(null));
  const [score, setScore] = useState(0); // points DANS le niveau courant
  const [level, setLevel] = useState(1);
  const [lives, setLives] = useState(3);
  const [phase, setPhase] = useState<"intro" | "play" | "won" | "lost">("intro");
  const [reward, setReward] = useState<Drop>(null);
  const idRef = useRef(0);
  const phaseRef = useRef(phase);
  useEffect(() => { phaseRef.current = phase; }, [phase]);
  const sfx = useRef<GameAudio | null>(null);
  if (!sfx.current) sfx.current = createGameAudio();
  useEffect(() => () => sfx.current?.dispose(), []);

  // Faces = créatures de l'enfant (possédées d'abord), comme le Memory.
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${apiBase}/api/child/collection`);
        if (r.ok) {
          const d = await r.json();
          const all: { emoji: string; owned: boolean }[] = [];
          for (const room of Object.values<any>(d.rooms)) for (const c of room.creatures) all.push(c);
          const seen = new Set<string>();
          const dedup = all.filter((c) => (seen.has(c.emoji) ? false : (seen.add(c.emoji), true)));
          dedup.sort((a, b) => Number(b.owned) - Number(a.owned));
          if (dedup.length >= 4) setFaces(dedup.slice(0, 10).map((c) => c.emoji));
        }
      } catch { /* offline → fallback */ }
    })();
  }, [apiBase]);

  const start = () => {
    setScore(0); setLevel(1); setLives(3); setReward(null);
    setHoles(Array(HOLES).fill(null));
    setPhase("play");
  };

  const onWin = useCallback(async () => {
    setPhase("won");
    setHoles(Array(HOLES).fill(null));
    sfx.current?.win();
    try {
      const r = await fetch(`${apiBase}/api/child/minigame/reward`, { method: "POST" });
      const d = await r.json();
      if (d && d.drop) setReward(d.drop);
    } catch { /* pas de récompense si offline */ }
  }, [apiBase]);

  // Boucle d'apparition : une créature (ou bombe) surgit dans un terrier libre,
  // puis se cache toute seule après popMs (la rater ne coûte rien).
  useEffect(() => {
    if (phase !== "play") return;
    const t = TUNING[level - 1] || TUNING[TUNING.length - 1];
    const spawn = setInterval(() => {
      setHoles((cur) => {
        const free: number[] = [];
        cur.forEach((h, i) => { if (!h) free.push(i); });
        if (!free.length) return cur;
        const slot = free[Math.floor(Math.random() * free.length)];
        const bomb = Math.random() < t.bombP;
        const id = ++idRef.current;
        const pop: Pop = { id, bomb, emoji: bomb ? "💣" : faces[Math.floor(Math.random() * faces.length)] };
        const next = cur.slice();
        next[slot] = pop;
        // La créature repart d'elle-même si personne ne la tape.
        setTimeout(() => {
          if (phaseRef.current !== "play") return;
          setHoles((c2) => (c2[slot]?.id === id ? c2.map((h, i) => (i === slot ? null : h)) : c2));
        }, t.popMs);
        return next;
      });
    }, t.spawnMs);
    return () => clearInterval(spawn);
  }, [phase, level, faces]);

  const tap = (i: number) => {
    if (phase !== "play") return;
    const pop = holes[i];
    if (!pop) return;
    setHoles((cur) => cur.map((h, idx) => (idx === i ? null : h)));
    if (pop.bomb) {
      try { (navigator as any).vibrate?.(160); } catch {}
      sfx.current?.hit();
      setLives((l) => {
        if (l - 1 <= 0) { setPhase("lost"); setHoles(Array(HOLES).fill(null)); sfx.current?.lose(); }
        return l - 1;
      });
      return;
    }
    try { (navigator as any).vibrate?.(30); } catch {}
    sfx.current?.point();
    setScore((s) => {
      const ns = s + 1;
      if (ns >= LEVEL_GOAL) {
        if (level >= LEVELS) { onWin(); return ns; }
        setLevel((lv) => lv + 1);
        return 0; // nouveau niveau, compteur remis
      }
      return ns;
    });
  };

  const rar = reward?.rarity;
  const rewardCol = rar === "legendary" ? "#ffd43b" : rar === "rare" ? "var(--kid-cyan)" : "var(--kid-text-3)";
  const total = (level - 1) * LEVEL_GOAL + score;

  return (
    <div style={S.screen}>
      <style>{CSS}</style>
      <div style={S.header}>
        <button style={S.close} onClick={onClose} aria-label="Fermer">✕</button>
        <div style={S.title}>TAPE LA CRÉATURE !</div>
        <div style={S.hud}>
          <span style={{ color: "var(--kid-cyan)", fontWeight: 800 }}>NIVEAU {level}</span>
          <span>{"❤️".repeat(Math.max(0, lives))}</span>
        </div>
      </div>

      {/* Barre de progression du niveau */}
      <div style={S.progressWrap}>
        <div style={{ ...S.progressBar, width: `${(score / LEVEL_GOAL) * 100}%` }} />
      </div>

      <div style={S.boardWrap}>
        <div style={S.board}>
          {holes.map((pop, i) => (
            <button key={i} className="tap-hole" onClick={() => tap(i)} aria-label={pop ? (pop.bomb ? "bombe" : "créature") : "terrier vide"}>
              <span className="tap-burrow">🕳️</span>
              {pop && <span className={`tap-pop${pop.bomb ? " bomb" : ""}`}>{pop.emoji}</span>}
            </button>
          ))}
        </div>
      </div>

      {phase === "intro" && (
        <div style={S.winOverlay} onClick={start}>
          <div style={S.winCard}>
            <div style={{ fontSize: 40 }}>🕳️</div>
            <div style={{ color: "var(--kid-cyan)", fontWeight: 800, fontSize: 20 }}>TAPE LA CRÉATURE !</div>
            <div style={{ ...S.winSub, lineHeight: 1.5, marginTop: 10 }}>
              Les créatures surgissent des terriers : tape-les pour marquer !<br />
              ⚠️ À partir du niveau 2, ne touche PAS les bombes 💣 (3 vies).<br />
              3 niveaux de {LEVEL_GOAL} points pour gagner.
            </div>
            <button style={S.replay} onClick={(e) => { e.stopPropagation(); start(); }}>▶ JOUER</button>
          </div>
        </div>
      )}

      {phase === "won" && (
        <div style={S.winOverlay} onClick={start}>
          <div style={S.winCard}>
            <div style={{ fontSize: 40 }}>🏆</div>
            <div style={{ color: "var(--kid-cyan)", fontWeight: 800, fontSize: 20 }}>GAGNÉ !</div>
            <div style={S.winSub}>{LEVELS * LEVEL_GOAL} créatures tapées, niveaux terminés !</div>
            {reward ? (
              <div style={{ ...S.rewardBox, borderColor: rewardCol }}>
                <div style={{ fontSize: 44 }}>{reward.emoji}</div>
                <div style={{ fontWeight: 700 }}>{reward.name}</div>
                <div style={{ color: rewardCol, fontSize: 12, fontFamily: "var(--kid-font-mono)", letterSpacing: "0.14em" }}>
                  CRÉATURE DÉBLOQUÉE&nbsp;!
                </div>
              </div>
            ) : (
              <div style={S.winSub}>Pas de créature cette fois — rejoue pour retenter&nbsp;!</div>
            )}
            <button style={S.replay} onClick={(e) => { e.stopPropagation(); start(); }}>🔄 Rejouer</button>
            <button style={S.leave} onClick={(e) => { e.stopPropagation(); onClose(); }}>Quitter</button>
          </div>
        </div>
      )}

      {phase === "lost" && (
        <div style={S.winOverlay} onClick={start}>
          <div style={S.winCard}>
            <div style={{ fontSize: 40 }}>💥</div>
            <div style={{ color: "var(--kid-cyan)", fontWeight: 800, fontSize: 20 }}>BOUM ! Plus de vies</div>
            <div style={S.winSub}>Score : {total} — les bombes, on les évite !</div>
            <button style={S.replay} onClick={(e) => { e.stopPropagation(); start(); }}>🔄 Réessayer</button>
            <button style={S.leave} onClick={(e) => { e.stopPropagation(); onClose(); }}>Quitter</button>
          </div>
        </div>
      )}
    </div>
  );
}

const CSS = `
.tap-hole { position: relative; aspect-ratio: 1; border-radius: 18px; border: 1.5px solid var(--kid-cyan-40); background: rgba(0,212,255,0.05); cursor: pointer; padding: 0; overflow: hidden; }
.tap-hole .tap-burrow { position: absolute; inset: 0; display: flex; align-items: flex-end; justify-content: center; font-size: clamp(26px, 10vw, 44px); opacity: 0.5; padding-bottom: 6%; }
.tap-hole .tap-pop { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: clamp(34px, 13vw, 58px); animation: tap-rise .18s ease-out; line-height: 1; }
.tap-hole .tap-pop.bomb { animation: tap-rise .18s ease-out, tap-tick .5s ease-in-out infinite; }
.tap-hole:active { transform: scale(0.94); }
@keyframes tap-rise { from { transform: translateY(45%) scale(.6); opacity: 0; } to { transform: none; opacity: 1; } }
@keyframes tap-tick { 0%,100% { transform: rotate(-6deg); } 50% { transform: rotate(6deg); } }
@keyframes tap-pop-in { from { transform: scale(.6); opacity: 0 } to { transform: scale(1); opacity: 1 } }
`;

const S: Record<string, React.CSSProperties> = {
  screen: { position: "fixed", inset: 0, zIndex: 62, background: "var(--kid-bg)", display: "flex", flexDirection: "column", color: "var(--kid-text)", fontFamily: "var(--kid-font-sans, system-ui)" },
  header: { display: "flex", alignItems: "center", gap: 12, padding: "16px 16px 8px", borderBottom: "1px solid var(--kid-border)" },
  close: { width: 40, height: 40, borderRadius: 12, border: "1px solid var(--kid-cyan-40)", background: "transparent", color: "var(--kid-cyan)", fontSize: 18, cursor: "pointer" },
  title: { flex: 1, fontFamily: "var(--kid-font-mono)", letterSpacing: "0.12em", fontSize: 14, color: "var(--kid-cyan)" },
  hud: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, fontFamily: "var(--kid-font-mono)", fontSize: 12 },
  progressWrap: { height: 8, margin: "10px 16px 0", borderRadius: 999, background: "rgba(255,255,255,0.07)", overflow: "hidden" },
  progressBar: { height: "100%", borderRadius: 999, background: "var(--kid-cyan)", transition: "width .25s ease" },
  boardWrap: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
  board: { width: "100%", maxWidth: 400, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 },
  winOverlay: { position: "fixed", inset: 0, zIndex: 64, background: "rgba(3,5,9,0.8)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
  winCard: { textAlign: "center", padding: "26px 30px", borderRadius: 20, border: "1px solid var(--kid-cyan-40)", background: "var(--kid-bg)", animation: "tap-pop-in .35s ease", maxWidth: 340, width: "100%" },
  winSub: { color: "var(--kid-text-3)", fontSize: 13, marginTop: 4 },
  rewardBox: { margin: "16px auto 4px", padding: "14px", borderRadius: 16, border: "2px solid var(--kid-cyan)", display: "flex", flexDirection: "column", gap: 4, alignItems: "center" },
  replay: { display: "block", width: "100%", marginTop: 18, padding: "13px", borderRadius: 14, border: "none", background: "linear-gradient(180deg, var(--kid-cyan), #008cbe)", color: "#04222c", fontSize: 16, fontWeight: 800, cursor: "pointer" },
  leave: { display: "block", width: "100%", marginTop: 10, padding: "11px", borderRadius: 12, border: "1px solid var(--kid-cyan-40)", background: "transparent", color: "var(--kid-text-2)", fontSize: 14, cursor: "pointer" },
};
